import { Heading } from '@mm/components';
import { useResolutionInfo } from '@mm/hooks';
import React, { useEffect } from 'react';
import { MOBILE_SMALL } from '../../../editor/constants/screenSize';
import styles from './NewBillingModalShopify.module.css';
import { CloseOutlined } from '@ant-design/icons';
import { useSelector } from 'react-redux';
import {
  activeCompanySelector,
  activeFacebookPageIdSelector,
  selectActiveCompanyId,
  selectIsShopifyContext,
} from '@mm/state/selectors/viewSelectors';
import { selectSessionSelector } from '@mm/state/selectors/userSelector';
import { useUsersCRUDActions } from '@mm/state/actions/UsersCRUDActions';
import { useModalActions } from '@mm/state/modules/modalsState/actions';
import { useConnectedPageCRUDActions } from '@mm/state/actions/ConnectedPageCRUDActions';
import { Logo } from '../../../layout/BotBuilderSidebar/Logo/Logo';
import { INewBillingModal2023Props } from '@mm/state/modules/modalsState/props';
import iconiqLogo from 'assets/iconiq-logo.png';
import { useLandmineOpensHelpers } from '../UniversalUpgradeModal/useLandmineOpensHelpers';
import classNames from 'classnames';
import { BillingPlans } from '../../../dashboard/pages/SettingsPage/BillingPanel2023/BillingPlans/BillingPlans';
import { activeFacebookPageSelector } from '@mm/state/selectors/facebookPageSelector';
import { isBilling2023Subscription } from '@mm/state/model/SubscriptionModel';
import { G2BadgeTop50, MetaBadge } from '@mm/icons';
import { useShopifyBillingPlans } from '../../../dashboard/pages/SettingsPage/BillingPanel2023/BillingPlans/use-shopify-billing-plans';

export const NewBillingModalShopify = (props: INewBillingModal2023Props) => {
  const isShopify = useSelector(selectIsShopifyContext);

  const {
    landmineName,
    withCloseButton = true,
    hideFreePlan = true,
    className,
    withLogo = false,
    heading,
    subheading,
    css,
    onSuccessBilling,
  } = props;
  const companyId = useSelector(selectActiveCompanyId);
  const company = useSelector(activeCompanySelector);
  const { id: userId, userAttributes } = useSelector(selectSessionSelector);
  const activeFacebookPageId = useSelector(activeFacebookPageIdSelector);
  const activeFacebookPage = useSelector(activeFacebookPageSelector);
  const isAgency = company?.companyAttributes?.agency;

  const headerText = heading
    ? heading
    : isShopify
    ? 'Choose Plan'
    : isAgency
    ? 'Agency Plans'
    : 'Find your perfect customers';
  const subheaderText = subheading
    ? subheading
    : isShopify
    ? ''
    : isAgency
    ? 'Enhance your agency with Customers.ai'
    : 'Flexible plans for Sales Outreach Automation solutions for your growing B2C businesses';
  const { updateUserAttributes } = useUsersCRUDActions();
  const { retrievePage } = useConnectedPageCRUDActions();

  const { landmineOpenId, landmineOpensCRUDActions } =
    useLandmineOpensHelpers(landmineName);

  const { closeModal } = useModalActions();
  const { screenWidth } = useResolutionInfo();
  const isMobile = screenWidth <= MOBILE_SMALL;

  const updateAttributes = () => {
    if (!userAttributes?.seenNewBilling2023Modal) {
      updateUserAttributes(companyId, userId, {
        seenNewBilling2023Modal: true,
      });
    }
  };

  const pollTillOutgoingMessagesUpdated = async () => {
    const prevMessagesLimit = activeFacebookPage?.outgoingMessagesLimit;

    let pollingAttempts = 0;

    if (activeFacebookPageId) {
      const pollInterval = 3000; // 3 seconds
      const maxAttempts = 3;

      const pollRequest = async () => {
        try {
          const result = await retrievePage(
            activeFacebookPageId,
            false,
            false,
            true,
          );
          const currentMessagesLimit = result?.outgoingMessagesLimit;

          if (
            prevMessagesLimit !== currentMessagesLimit ||
            (result?.subscriptions?.[0] &&
              isBilling2023Subscription(result?.subscriptions?.[0]))
          ) {
            return;
          }

          pollingAttempts += 1;

          if (pollingAttempts < maxAttempts) {
            setTimeout(pollRequest, pollInterval);
          } else {
            return;
          }
        } catch (error) {
          throw new Error(`Request error: ${error}`);
        }
      };

      setTimeout(pollRequest, 5000);
    }
  };

  const onBillingSuccess = async () => {
    if (landmineOpenId) {
      landmineOpensCRUDActions.update(landmineOpenId, true);
    }
    closeModal();
    await onSuccessBilling?.();

    pollTillOutgoingMessagesUpdated();
  };

  useEffect(() => {
    updateAttributes();
  }, []);

  const shopifyBillingPlans = useShopifyBillingPlans();

  const shopifyAppPlans = (
    <>
      <BillingPlans
        plans={shopifyBillingPlans.plans}
        onBillingSuccess={onBillingSuccess}
        landmineOpenId={landmineOpenId}
        hideFreePlan={false}
        hideMonthlyManagementFee
      />
    </>
  );

  return (
    <div className={classNames(styles.container, className)}>
      {withCloseButton && (
        <CloseOutlined
          className={styles.closeIcon}
          onClick={() => closeModal()}
        />
      )}
      {withLogo && <Logo className={styles.appLogo} />}

      {!isMobile && (
        <div
          className={classNames(styles.badges, {
            [styles.badgesRight]: withLogo,
          })}
        >
          <img src={iconiqLogo} className={styles.logo} />
          <G2BadgeTop50 />
          <MetaBadge className={styles.meta} />
        </div>
      )}
      <div className={classNames(styles.header, css?.header)}>
        <Heading size={1} className={styles.heading}>
          {headerText}
          <div className={styles.subheading}>{subheaderText}</div>
        </Heading>
      </div>

      {shopifyAppPlans}
    </div>
  );
};
